/**
 * Open-data commons gate (issue #72, CLIENT section).
 *
 * The trail manifest MAY carry a `commons` block once the backend's
 * export-commons script has published a CC-BY-SA snapshot to the beta
 * PWA bucket:
 *
 *   commons: {
 *     version: "<export version>",
 *     license: "CC-BY-SA-4.0",
 *     observations_url: "https://…/commons/v<version>/observations.geojson",
 *     metadata_url:     "https://…/commons/v<version>/metadata.json",
 *   }
 *
 * PRESENCE of a valid `commons` block is the ONE signal that renders
 * the "Open data" download row in the Sources sheet; absence (or any
 * malformed block) hides the row entirely, so this change can merge
 * BEFORE the first publish without ever advertising a dead link. No
 * other flag, env var, or URL param reveals the row — same design as
 * the tile-mode gate (lib/tile-mode.ts), for the same reason: the two
 * states can't disagree.
 */

export interface TrailCommonsConfig {
  version: string;
  /** Compilation license id shown next to the link (e.g. "CC-BY-SA-4.0"). */
  license: string;
  observations_url: string;
  metadata_url: string;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * True for URL shapes a plain <a download> can actually fetch. http(s)
 * absolute URLs are what the export publishes (absolute CloudFront
 * URLs per the manifest contract); root-relative paths are accepted so
 * e2e fixtures / a same-origin bucket layout also work.
 */
function isFetchableUrl(v: unknown): boolean {
  if (!isNonEmptyString(v)) return false;
  return /^https?:\/\//.test(v) || v.startsWith('/');
}

/**
 * THE open-data gate. Returns the validated commons config when the
 * manifest carries a usable `commons` block, else null (row hidden).
 * Deliberately strict: a partially-formed block (missing/empty URL,
 * missing version or license) is treated as "no commons" rather than
 * rendering a download link we can't honestly label or serve.
 */
export function commonsConfigOf(manifest: unknown): TrailCommonsConfig | null {
  if (!manifest || typeof manifest !== 'object') return null;
  const commons = (manifest as { commons?: unknown }).commons;
  if (!commons || typeof commons !== 'object') return null;
  const c = commons as Record<string, unknown>;
  if (!isNonEmptyString(c.version) || !isNonEmptyString(c.license)) return null;
  if (!isFetchableUrl(c.observations_url) || !isFetchableUrl(c.metadata_url)) {
    return null;
  }
  return {
    version: c.version,
    license: c.license,
    observations_url: c.observations_url as string,
    metadata_url: c.metadata_url as string,
  };
}
